import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
  LanguageIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { downloadBillOfLadingPDF } from '../../../services/logistics/bill-of-lading-generator';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../ui/LoadingSpinner';

const BOL_LANGUAGE_STORAGE_KEY = 'bol_preferred_language';

const BillOfLadingModal = ({ shipment, onClose }) => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(() => {
    // Prefer the user's last BOL language choice, otherwise fall back to the
    // app's current i18n language.
    let savedLanguage = null;
    try {
      savedLanguage = localStorage.getItem(BOL_LANGUAGE_STORAGE_KEY);
    } catch {
      // localStorage unavailable (private mode, etc.) — ignore.
    }
    const initialLanguage = savedLanguage === 'ar' || savedLanguage === 'en'
      ? savedLanguage
      : (i18n.language?.startsWith('ar') ? 'ar' : 'en');
    return {
      language: initialLanguage,
      includeAmount: false,
    };
  });

  const handleLanguageSelect = (value) => {
    setOptions(p => ({ ...p, language: value }));
    try {
      localStorage.setItem(BOL_LANGUAGE_STORAGE_KEY, value);
    } catch {
      // Non-critical — selection still works for this session.
    }
  };

  const handleGeneratePDF = async () => {
    if (!shipment || !currentCompany?.id) {
      toast.error(t('common.error', 'An error occurred'));
      return;
    }
    setLoading(true);
    try {
      await downloadBillOfLadingPDF(shipment, currentCompany, options);
      toast.success(t('shipments.bill_of_lading_generated', 'Bill of Lading generated successfully'));
      onClose();
    } catch (error) {
      console.error('Error generating Bill of Lading:', error);
      toast.error(error.message || t('shipments.bill_of_lading_error', 'Failed to generate Bill of Lading'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 relative z-10">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900">
              {t('shipments.bill_of_lading', 'Bill of Lading')}
            </h3>
          </div>
          <button onClick={onClose} disabled={loading} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">

          {/* Shipment info badge */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-3">
            <p className="text-xs text-blue-500 font-medium mb-0.5">{t('shipments.shipment_number', 'Shipment')}</p>
            <p className="font-bold text-blue-900 text-sm">{shipment.trackingNumber || shipment.shipmentNumber}</p>
            <p className="text-sm text-blue-700 mt-0.5">{shipment.clientName}</p>
          </div>

          {/* Language */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              <LanguageIcon className="w-3.5 h-3.5" />
              {t('common.language', 'Language')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'en', label: 'English' },
                { value: 'ar', label: 'العربية' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleLanguageSelect(value)}
                  className={`flex items-center justify-center py-2.5 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    options.language === value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Include Amount */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={options.includeAmount}
              onChange={(e) => setOptions(p => ({ ...p, includeAmount: e.target.checked }))}
              className="mt-0.5 w-4 h-4 text-blue-600 rounded"
            />
            <div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                {t('shipments.include_amount', 'Include Charges')}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {t('shipments.include_amount_note', 'Include freight cost breakdown in the document')}
              </p>
            </div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-5">
          <button onClick={onClose} disabled={loading} className="flex-1 btn btn-ghost text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
          <button onClick={handleGeneratePDF} disabled={loading} className="flex-1 btn btn-primary text-sm flex items-center justify-center gap-2">
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>{t('common.generating', 'Generating…')}</span>
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-4 h-4" />
                {t('shipments.download_bill_of_lading', 'Download PDF')}
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default BillOfLadingModal;
